import React, { useEffect, useState } from 'react';

export const TOOLTIP_ATTR = 'data-tooltip';

type TooltipState = {
    text: string;
    x: number;
    y: number;
};

/**
 * App-wide hover tooltip. Any element anywhere in the DOM — SVG shape, div,
 * span, whatever — registers itself just by carrying a `data-tooltip="..."`
 * attribute; a single pair of document-level listeners here does the rest,
 * so no per-element event wiring is needed at the call site.
 *
 * Shows instantly on hover (no native `title`-attribute delay) and tracks
 * the cursor, rendered as a callout: a dark box with a line running from
 * its bottom-middle down to the pointer.
 *
 * Mount exactly once near the app root (see App.tsx) — from there it works
 * globally regardless of which route or panel is active.
 */
export default function Tooltip() {
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);

    useEffect(() => {
        function handleMouseMove(event: MouseEvent) {
            const target = event.target instanceof Element ? event.target.closest(`[${TOOLTIP_ATTR}]`) : null;
            if (!target) {
                setTooltip(null);
                return;
            }
            setTooltip({ text: target.getAttribute(TOOLTIP_ATTR) || '', x: event.clientX, y: event.clientY });
        }

        function handleWindowLeave() {
            setTooltip(null);
        }

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseleave', handleWindowLeave);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseleave', handleWindowLeave);
        };
    }, []);

    if (!tooltip) return null;

    return (
        <div className='app-tooltip' style={{ left: tooltip.x, top: tooltip.y }}>
            <div className='app-tooltip__box'>{tooltip.text}</div>
            <div className='app-tooltip__pointer' />
        </div>
    );
}
